import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Link2, Wand2, AlertCircle, AlertTriangle, Info, CheckCircle2, Copy, ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { apiV1 } from '@/api/client';

const EMPTY = {
  utm_source: '', utm_medium: '', utm_campaign: '', utm_term: '', utm_content: '',
};

const SEVERITY = {
  error: { icon: AlertCircle, cls: 'text-destructive', badge: 'destructive' },
  warning: { icon: AlertTriangle, cls: 'text-amber-600', badge: 'secondary' },
  info: { icon: Info, cls: 'text-muted-foreground', badge: 'outline' },
};

export default function UTMBuilder() {
  const [reference, setReference] = useState({ params: [], mediums: [], naming_rules: [] });
  const [baseUrl, setBaseUrl] = useState('');
  const [values, setValues] = useState(EMPTY);
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [pasteUrl, setPasteUrl] = useState('');
  const debounceRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiV1.get('/utm/reference');
        if (data?.success) setReference(data.data);
      } catch (e) {
        // Guidance is a nice-to-have; the validator still works without it.
      }
    })();
  }, []);

  const runValidate = useCallback(async (vals, base) => {
    const hasAny = base || Object.values(vals).some((v) => v && v.trim());
    if (!hasAny) { setResult(null); return; }
    setChecking(true);
    try {
      const { data } = await apiV1.post('/utm/validate', { ...vals, base_url: base || null });
      if (data?.success) setResult(data.data);
    } catch (e) {
      // leave previous result
    } finally {
      setChecking(false);
    }
  }, []);

  // Debounced live validation as the user types.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runValidate(values, baseUrl), 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [values, baseUrl, runValidate]);

  const setField = (key, val) => setValues((p) => ({ ...p, [key]: val }));

  const applyFixes = () => {
    if (!result?.normalized) return;
    setValues({ ...EMPTY, ...result.normalized });
    toast.success('Applied best-practice fixes');
  };

  const copy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const checkPastedUrl = async () => {
    if (!pasteUrl.trim()) return;
    setChecking(true);
    try {
      const { data } = await apiV1.post('/utm/parse', { url: pasteUrl.trim() });
      if (data?.success) {
        const ex = data.data.extracted || {};
        setBaseUrl(data.data.base_url || '');
        setValues({ ...EMPTY, ...ex });
        setResult(data.data);
        toast.success('Pulled the UTMs out of that URL — review below');
      }
    } catch (e) {
      toast.error('Could not read that URL');
    } finally {
      setChecking(false);
    }
  };

  const paramHelp = (key) => reference.params.find((p) => p.key === key);
  const issuesFor = (key) => (result?.issues || []).filter((i) => i.param === key);
  const hasErrors = result && !result.valid;

  const Field = ({ k }) => {
    const help = paramHelp(k);
    const issues = issuesFor(k);
    return (
      <div className="space-y-1.5">
        <Label htmlFor={k} className="flex items-center gap-2">
          {help?.name || k}
          {help?.required && <span className="text-destructive">*</span>}
        </Label>
        <Input
          id={k}
          data-testid={`utm-${k}`}
          placeholder={help?.examples?.[0] || ''}
          value={values[k]}
          onChange={(e) => setField(k, e.target.value)}
        />
        {help && (
          <p className="text-xs text-muted-foreground">
            <span className="font-medium">{help.question}</span> {help.description}
          </p>
        )}
        {issues.map((i, idx) => {
          const S = SEVERITY[i.severity] || SEVERITY.info;
          const Icon = S.icon;
          return (
            <div key={idx} className={`flex items-start gap-1.5 text-xs ${S.cls}`}>
              <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                {i.message}
                {i.suggestion && (
                  <button
                    type="button"
                    className="ml-1 underline font-medium"
                    onClick={() => setField(k, i.suggestion)}
                  >
                    use “{i.suggestion}”
                  </button>
                )}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="utm-builder-page">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Wand2 className="h-6 w-6 text-primary" />
          UTM Builder &amp; Validator
        </h1>
        <p className="text-muted-foreground">
          Build campaign tracking links the right way — and catch the common mistakes
          (capitals, spaces, mixing up source and medium) before they split your reporting.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: builder / checker */}
        <Card>
          <CardHeader>
            <CardTitle>Your campaign tags</CardTitle>
            <CardDescription>Fill these in, or paste an existing link to check it.</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="build">
              <TabsList className="mb-4">
                <TabsTrigger value="build" data-testid="utm-tab-build">Build</TabsTrigger>
                <TabsTrigger value="check" data-testid="utm-tab-check">Check a URL</TabsTrigger>
              </TabsList>

              <TabsContent value="build" className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="base_url" className="flex items-center gap-2">
                    <Link2 className="h-4 w-4 text-primary" /> Landing URL
                  </Label>
                  <Input
                    id="base_url"
                    data-testid="utm-base-url"
                    placeholder="https://pamgolding.co.za/listing/123"
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                  />
                </div>

                <Field k="utm_source" />
                <Field k="utm_medium" />
                {/* Quick-pick standard mediums */}
                {reference.mediums.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {reference.mediums.map((m) => (
                      <button
                        key={m.value}
                        type="button"
                        title={m.description}
                        onClick={() => setField('utm_medium', m.value)}
                        className={`text-xs rounded-full border px-2.5 py-1 transition-colors hover:bg-muted ${
                          values.utm_medium === m.value ? 'border-primary bg-primary/10 text-primary' : 'border-border'
                        }`}
                      >
                        {m.value}
                      </button>
                    ))}
                  </div>
                )}
                <Field k="utm_campaign" />
                <Field k="utm_term" />
                <Field k="utm_content" />
              </TabsContent>

              <TabsContent value="check" className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="paste_url">Paste a link with UTMs</Label>
                  <Input
                    id="paste_url"
                    data-testid="utm-paste-url"
                    placeholder="https://...?utm_source=...&utm_medium=..."
                    value={pasteUrl}
                    onChange={(e) => setPasteUrl(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && checkPastedUrl()}
                  />
                </div>
                <Button onClick={checkPastedUrl} disabled={!pasteUrl.trim() || checking} data-testid="utm-check-btn">
                  Check this URL
                </Button>
                <p className="text-xs text-muted-foreground">
                  We'll pull out the tags and check them — results appear on the right, and you can
                  switch to the Build tab to fix them.
                </p>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Right: results */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Result
                {result && (
                  hasErrors
                    ? <Badge variant="destructive" data-testid="utm-status">Needs fixing</Badge>
                    : <Badge className="bg-emerald-600 hover:bg-emerald-600" data-testid="utm-status">Looks good</Badge>
                )}
              </CardTitle>
              <CardDescription>
                {result ? `${result.issues.length} issue${result.issues.length === 1 ? '' : 's'} found`
                        : 'Start typing to validate.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {result && (result.issues.length === 0) && (
                <div className="flex items-center gap-2 text-sm text-emerald-600">
                  <CheckCircle2 className="h-4 w-4" /> No problems — these tags follow best practice.
                </div>
              )}

              {/* Best-practice version + apply */}
              {result?.normalized && Object.keys(result.normalized).length > 0 && (
                <div className="rounded-md border p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Best-practice version</span>
                    <Button size="sm" variant="outline" onClick={applyFixes} data-testid="utm-apply-fixes">
                      <Wand2 className="h-3.5 w-3.5 mr-1" /> Apply fixes
                    </Button>
                  </div>
                  <div className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-sm">
                    {Object.entries(result.normalized).map(([k, v]) => (
                      <React.Fragment key={k}>
                        <span className="text-muted-foreground">{k.replace('utm_', '')}</span>
                        <span className="font-mono">{v}</span>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}

              {/* Generated URL */}
              {result?.tracked_url && (
                <div className="space-y-1.5">
                  <Label>Tracking URL</Label>
                  <div className="flex gap-2">
                    <code className="flex-1 break-all rounded-md bg-muted px-3 py-2 text-xs" data-testid="utm-tracked-url">
                      {result.tracked_url}
                    </code>
                    <Button size="icon" variant="outline" onClick={() => copy(result.tracked_url)} title="Copy">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  {result.raw_tracked_url && result.raw_tracked_url !== result.tracked_url && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <ArrowRight className="h-3 w-3" /> Tidied up from what you entered.
                    </p>
                  )}
                </div>
              )}

              {!result && (
                <p className="text-sm text-muted-foreground">
                  Enter a landing URL and your campaign tags on the left — validation runs as you type.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Guidance */}
          {reference.naming_rules.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">The rules of clean UTMs</CardTitle>
                <CardDescription>Why source and medium aren't the same thing.</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {reference.naming_rules.map((r, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
